import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { supabase } from './supabase';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// UUID validation helper
export function isValidUUID(uuid: string): boolean {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(uuid);
}

// Cache for signed URLs with preloading support
const urlCache = new Map<string, { url: string; expires: number }>();
const preloadCache = new Map<string, Promise<void>>();

// Preload images in the background
async function preloadImage(url: string): Promise<void> {
  if (preloadCache.has(url)) {
    return preloadCache.get(url);
  }

  const preloadPromise = new Promise<void>((resolve) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => resolve(); // Resolve on error to prevent hanging
    img.src = url;
  });

  preloadCache.set(url, preloadPromise);
  await preloadPromise;
  preloadCache.delete(url);
}

export async function getSignedUrl(path: string | null, preload = false): Promise<string | null> {
  if (!path) return null;
  if (!path.includes('kunden/')) return path;

  const now = Date.now();
  const cached = urlCache.get(path);
  
  // Return cached URL if it's still valid (expires in 55 minutes)
  if (cached && cached.expires > now) {
    if (preload) void preloadImage(cached.url);
    return cached.url;
  }

  try {
    const filePath = path.split('kunden/')[1];
    const { data } = await supabase.storage
      .from('kunden')
      .createSignedUrl(filePath, 3600);

    if (data?.signedUrl) {
      // Cache the URL with expiration (55 minutes)
      const signedUrl = data.signedUrl;
      urlCache.set(path, {
        url: signedUrl,
        expires: now + 55 * 60 * 1000
      });

      if (preload) void preloadImage(signedUrl);
      return signedUrl;
    }
  } catch (error) {
    console.error('Error getting signed URL:', error);
  }

  return null;
}

// Batch preload multiple images
export async function preloadImages(paths: string[]) {
  const promises = paths
    .filter(Boolean)
    .map(path => getSignedUrl(path, true));
  
  return Promise.all(promises);
}

// Component preloading cache
const componentCache = new Map<string, Promise<any>>();

// Preload component function
export function preloadComponent(importFn: () => Promise<any>): Promise<any> {
  const key = importFn.toString();
  
  if (componentCache.has(key)) {
    return componentCache.get(key)!;
  }

  const promise = importFn().catch((error) => {
    // Remove from cache on error so it can be retried
    componentCache.delete(key);
    console.warn('Failed to preload component:', error);
    return null;
  });

  componentCache.set(key, promise);
  return promise;
}

// Route-specific preloading functions
export const preloadRoutes = {
  dashboard: () => preloadComponent(() => import('@/components/dashboard/Dashboard')),
  overview: () => preloadComponent(() => import('@/components/pages/UebersichtPage')),
  development: () => preloadComponent(() => import('@/components/pages/EntwicklungPage')),
  todos: () => preloadComponent(() => import('@/components/pages/TodosPage')),
  academy: () => preloadComponent(() => import('@/components/academy/AcademyPage')),
  profile: () => preloadComponent(() => import('@/components/profile/ProfilePage')),
  community: () => preloadComponent(() => import('@/components/community/CommunityPage')),
  
  // Admin routes
  adminDashboard: () => preloadComponent(() => import('@/components/admin/AdminDashboard')),
  adminPage: () => preloadComponent(() => import('@/components/admin/AdminPage')),
  adminDebug: () => preloadComponent(() => import('@/components/admin/AdminDebugPage')),
};

// Clear expired cache entries
// Use a more WebContainer-friendly approach for cache cleanup
let cacheCleanupScheduled = false;

function scheduleCacheCleanup() {
  if (cacheCleanupScheduled) return;
  
  cacheCleanupScheduled = true;
  
  // Use requestIdleCallback if available, otherwise use requestAnimationFrame
  const cleanup = () => {
    const now = Date.now();
    for (const [key, value] of urlCache.entries()) {
      if (value.expires <= now) {
        urlCache.delete(key);
      }
    }
    cacheCleanupScheduled = false;
    
    // Schedule next cleanup
    setTimeout(() => {
      if (typeof requestIdleCallback !== 'undefined') {
        requestIdleCallback(cleanup);
      } else {
        requestAnimationFrame(cleanup);
      }
    }, 5 * 60 * 1000); // Clean every 5 minutes
  };
  
  if (typeof requestIdleCallback !== 'undefined') {
    requestIdleCallback(cleanup);
  } else {
    requestAnimationFrame(cleanup);
  }
}

// Start the cleanup cycle
scheduleCacheCleanup();